import { Box, Spinner } from '@chakra-ui/react';
import { useEffect, useRef } from 'react';
import { isEmpty } from 'lodash';
import { FaceLandmarker, DrawingUtils } from '@mediapipe/tasks-vision';

import { Condition } from 'Component/others';
import { faceTrackingText } from 'Const/monitoring/view';
import calculateBoundingBox from 'Helper/calculateBoundingBox';
import Canvas from 'UiKit/canvas';
import Video from 'UiKit/video';
import { useModal } from 'Store/modal';
import useFaceModel from 'Store/face-model';

import styles from './styles.module.css';

const SPOOFING_THRESHOLD = 180 * 1000;
const VIDEO_WIDTH = 640;
const VIDEO_HEIGHT = 360;
const DETECTION_INTERVAL = 150;

const FaceTracking = ({
  lastFacePositionRef,
  lastMovementTimeRef,
  className,
  isOpen,
  onTracking,
  isMonitoring,
  onCloseModal = () => {},
  width = '150px',
  height = '150px',
}) => {
  const { onOpenModal, onResetModal } = useModal();
  const { faceModel, isFaceModelReady } = useFaceModel();
  const videoRef = useRef();
  const streamRef = useRef();
  const canvasRef = useRef();
  const drawingUtilsRef = useRef(null);

  const handleCloseCamera = () => streamRef?.current?.getTracks().forEach((track) => track.stop());

  const handleOpenCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          width: { ideal: VIDEO_WIDTH },
          height: { ideal: VIDEO_HEIGHT },
          frameRate: { ideal: 24, max: 24 },
        },
      });

      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      videoRef.current.onloadedmetadata = () => videoRef.current?.play();

      onResetModal();
    } catch (error) {
      console.log(error);
      onCloseModal();

      if (error.name === 'NotAllowedError') {
        onOpenModal({
          isOpen: true,
          title: faceTrackingText.accessDeniedTitleText,
          contentHtmlDescription: faceTrackingText.accessDeniedDescText,
          type: 'confirm',
          showSecondaryButton: false,
          showPrimaryButton: false,
          isLockedModalOpen: true,
        });
      } else if (error.name === 'NotFoundError') {
        onOpenModal({
          isOpen: true,
          title: faceTrackingText.deviceNotFoundTitleText,
          contentDescription: faceTrackingText.deviceNotFoundDescText,
          type: 'confirm',
          showSecondaryButton: false,
          showPrimaryButton: false,
          isLockedModalOpen: true,
        });
      }
    }
  };

  const handlePictureInPicture = async () => {
    try {
      if (document?.visibilityState === 'hidden') await videoRef?.current?.requestPictureInPicture();
      else await document.exitPictureInPicture();
    } catch (error) {
      console.error('Gagal masuk PiP:', error);
    }
  };

  const detectFace = async () => {
    if (isEmpty(faceModel) || isEmpty(videoRef?.current)) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;

    if (videoWidth === 0 || videoHeight === 0 || video.readyState < 2) return;

    let isFaceDetected = false;
    let isSpoofing = false;

    if (canvas.width !== videoWidth || canvas.height !== videoHeight) {
      canvas.width = videoWidth;
      canvas.height = videoHeight;
      drawingUtilsRef.current = new DrawingUtils(canvas.getContext('2d'));
    }

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    try {
      const result = faceModel.detectForVideo(video, performance.now());
      const isValidFace = result?.faceLandmarks?.length > 0;

      if (isValidFace) {
        isFaceDetected = true;

        const landmarks = result.faceLandmarks[0];
        const { xMin, yMin, width, height } = calculateBoundingBox(landmarks, videoWidth, videoHeight);

        ctx.strokeStyle = 'green';
        ctx.lineWidth = 1;
        ctx.strokeRect(xMin, yMin, width, height);

        // Draw tesselation mesh connections
        if (drawingUtilsRef.current) {
          drawingUtilsRef.current.drawConnectors(landmarks, FaceLandmarker.FACE_LANDMARKS_TESSELATION, {
            color: 'rgba(0, 255, 255, 0.2)',
            lineWidth: 1,
          });
        }

        const currentPos = {
          x1: xMin,
          y1: yMin,
          x2: xMin + width,
          y2: yMin + height,
        };

        const lastPos = lastFacePositionRef.current;

        const currentPosition = {
          x1: currentPos.x1,
          y1: currentPos.y1,
          x2: currentPos.x2,
          y2: currentPos.y2,
        };

        const isMoved =
          !lastPos ||
          Math.abs(currentPosition?.x1 - lastPos?.x1) > 5 ||
          Math.abs(currentPosition?.y1 - lastPos?.y1) > 5 ||
          Math.abs(currentPosition?.x2 - lastPos?.x2) > 5 ||
          Math.abs(currentPosition?.y2 - lastPos?.y2) > 5;

        if (isMoved) lastMovementTimeRef.current = Date.now();

        if (Date.now() - lastMovementTimeRef.current > SPOOFING_THRESHOLD) {
          isFaceDetected = false;
          isSpoofing = true;
        }

        lastFacePositionRef.current = currentPosition;
      } else {
        isFaceDetected = false;
        isSpoofing = false;
        lastFacePositionRef.current = null;
      }

      onTracking(isFaceDetected, isSpoofing);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (isOpen && videoRef && faceModel) handleOpenCamera();

    return () => handleCloseCamera();

    /* eslint-disable-next-line */
  }, [isOpen, videoRef, faceModel]);

  useEffect(() => {
    const videoSize = videoRef?.current?.width > 0 && videoRef?.current?.height > 0;

    if (isOpen && videoSize) {
      const timer = setInterval(detectFace, DETECTION_INTERVAL);
      return () => clearInterval(timer);
    }
    /* eslint-disable-next-line */
  }, [faceModel, isOpen, videoRef, streamRef, isFaceModelReady]);

  useEffect(() => {
    if (isMonitoring) document.addEventListener('visibilitychange', handlePictureInPicture);

    return () => document.removeEventListener('visibilitychange', handlePictureInPicture);
  }, [isMonitoring]);

  return (
    <Box position="relative">
      <Condition
        statement={!isFaceModelReady}
        children={<Box className={styles['face-tracking__loader']} children={<Spinner color="var(--color-primary70)" />} />}
      />
      <Video width={width} height={height} className={className} ref={videoRef} id="video-stream" />
      <Canvas ref={canvasRef} width={width} height={height} className={styles['face-tracking__canvas']} />
    </Box>
  );
};

export default FaceTracking;
